import { type Editor } from "@tiptap/react";
import { type Node as ProseMirrorNode } from "@tiptap/pm/model";

const PX_PER_CHAR = 10;
const CJK_PX_PER_CHAR = 17;
const CELL_PADDING_H = 16;
const MIN_COL_WIDTH_FLOOR = 90;
const DEFAULT_TABLE_WIDTH = 700;
const TABLE_HORIZONTAL_MARGIN = 24;

export const TABLE_LAYOUT_META = "tableLayoutOnly";

// Hiragana/katakana, CJK ideographs, CJK symbols/punctuation, Hangul, fullwidth forms
const CJK_CHAR = /[\u1100-\u11ff\u2e80-\u303f\u3040-\u30ff\u3130-\u318f\u31f0-\u31ff\u3400-\u4dbf\u4e00-\u9fff\uac00-\ud7af\uf900-\ufaff\uff00-\uff60\uffe0-\uffe6]/;
const WHITESPACE = /\s/;

type TCellEntry = {
  pos: number;
  node: ProseMirrorNode;
  colIndex: number;
  colspan: number;
};

type TTableInfo = {
  cells: TCellEntry[];
  colCount: number;
};

function getEffectiveColCount(row: ProseMirrorNode): number {
  let count = 0;
  row.forEach((cell) => {
    count += (cell.attrs.colspan as number) || 1;
  });
  return count;
}

function collectTables(doc: ProseMirrorNode): Map<number, TTableInfo> {
  const tables = new Map<number, TTableInfo>();
  let currentTablePos = -1;
  let colIndex = 0;

  doc.descendants((node, pos) => {
    if (node.type.name === "table") {
      const firstRow = node.firstChild;
      currentTablePos = pos;
      tables.set(pos, {
        cells: [],
        colCount: firstRow ? getEffectiveColCount(firstRow) : 0
      });
      return true;
    }

    if (node.type.name === "tableRow") {
      colIndex = 0;
      return true;
    }

    if (node.type.name === "tableCell" || node.type.name === "tableHeader") {
      if (currentTablePos >= 0 && tables.has(currentTablePos)) {
        const colspan = (node.attrs.colspan as number) || 1;
        tables.get(currentTablePos)?.cells.push({ pos, node, colIndex, colspan });
        colIndex += colspan;
      }
      return false;
    }

    return true;
  });

  return tables;
}

function getExistingColWidths(table: TTableInfo): number[] | null {
  const widths = new Array<number>(table.colCount).fill(0);
  let hasAny = false;
  for (const cell of table.cells) {
    const cw = cell.node.attrs.colwidth;
    if (cw && Array.isArray(cw)) {
      for (let c = 0; c < cell.colspan && cell.colIndex + c < table.colCount; c++) {
        const w = cw[c];
        if (typeof w === "number" && w > 0) {
          widths[cell.colIndex + c] = w;
          hasAny = true;
        }
      }
    }
  }
  return hasAny ? widths : null;
}

function textPxWidth(text: string): number {
  let px = 0;
  for (const ch of text) px += CJK_CHAR.test(ch) ? CJK_PX_PER_CHAR : PX_PER_CHAR;
  return px;
}

// CJK text wraps at any character, so an unbreakable run is a whitespace-free
// stretch of non-CJK characters; a single CJK character is the widest CJK unit.
function longestUnbreakablePx(text: string): number {
  let max = 0;
  let run = 0;
  for (const ch of text) {
    if (WHITESPACE.test(ch)) {
      run = 0;
    } else if (CJK_CHAR.test(ch)) {
      run = 0;
      max = Math.max(max, CJK_PX_PER_CHAR);
    } else {
      run += PX_PER_CHAR;
      max = Math.max(max, run);
    }
  }
  return max;
}

function colMinWidth(unbreakablePx: number): number {
  return Math.max(unbreakablePx + CELL_PADDING_H, MIN_COL_WIDTH_FLOOR);
}

function getColFloors(table: TTableInfo): number[] {
  const { colCount, cells } = table;
  const maxUnbreakable = new Array<number>(colCount).fill(0);
  for (const cell of cells) {
    const px = longestUnbreakablePx(cell.node.textContent);
    for (let c = 0; c < cell.colspan && cell.colIndex + c < colCount; c++) {
      maxUnbreakable[cell.colIndex + c] = Math.max(maxUnbreakable[cell.colIndex + c], px);
    }
  }
  return maxUnbreakable.map(colMinWidth);
}

function tableHasBadColwidth(table: TTableInfo): boolean {
  const widths = getExistingColWidths(table);
  if (!widths) return false;
  const floors = getColFloors(table);
  return widths.some((w, i) => w > 0 && w < floors[i]);
}

// Shrinks to the available width by taking the excess only from columns with
// surplus above their floor, so short columns stay readable. Floors themselves
// are scaled only when even they alone don't fit.
function shrinkToFit(widths: number[], floors: number[], availableWidth: number): number[] {
  const clamped = widths.map((w, i) => Math.max(w, floors[i]));
  const total = clamped.reduce((sum, w) => sum + w, 0);
  if (total <= availableWidth) return clamped;

  const totalFloor = floors.reduce((sum, w) => sum + w, 0);
  if (totalFloor >= availableWidth) {
    const scale = availableWidth / totalFloor;
    return floors.map((w) => Math.max(1, Math.floor(w * scale)));
  }

  const surplus = clamped.map((w, i) => w - floors[i]);
  const totalSurplus = surplus.reduce((sum, w) => sum + w, 0);
  const keep = totalSurplus > 0 ? (availableWidth - totalFloor) / totalSurplus : 0;
  return clamped.map((w, i) => floors[i] + Math.floor(surplus[i] * keep));
}

function scaleToWidth(widths: number[], targetWidth: number): number[] {
  const total = widths.reduce((sum, w) => sum + w, 0);
  if (total <= 0) return widths;
  const scale = targetWidth / total;
  return widths.map((w) => Math.max(1, Math.round(w * scale)));
}

function computeColWidths(table: TTableInfo, availableWidth: number): number[] {
  const { colCount, cells } = table;
  const contentPx = new Array<number>(colCount).fill(0);
  const unbreakablePx = new Array<number>(colCount).fill(0);

  for (const cell of cells) {
    const text = cell.node.textContent;
    const avgPerCol = textPxWidth(text) / cell.colspan;
    const wordPx = longestUnbreakablePx(text);
    for (let c = 0; c < cell.colspan && cell.colIndex + c < colCount; c++) {
      contentPx[cell.colIndex + c] = Math.max(contentPx[cell.colIndex + c], avgPerCol);
      unbreakablePx[cell.colIndex + c] = Math.max(unbreakablePx[cell.colIndex + c], wordPx);
    }
  }

  const neededWidths = contentPx.map((px, i) => Math.max(px, unbreakablePx[i], PX_PER_CHAR));
  const totalNeeded = neededWidths.reduce((sum, w) => sum + w, 0);
  const floors = unbreakablePx.map(colMinWidth);

  const widths = neededWidths.map((needed, i) => {
    const width = Math.round((needed / totalNeeded) * availableWidth);
    return Math.max(width, floors[i]);
  });

  return shrinkToFit(widths, floors, availableWidth);
}

export function autoSetTableColumnWidths(editor: Editor, refit = false): void {
  const { doc } = editor.state;
  const tables = collectTables(doc);
  if (tables.size === 0) return;

  const editorWidth = editor.view.dom.clientWidth || DEFAULT_TABLE_WIDTH;
  const availableWidth = Math.max(MIN_COL_WIDTH_FLOOR, editorWidth - TABLE_HORIZONTAL_MARGIN);
  let hasChanges = false;
  const { tr } = editor.state;

  for (const [, table] of tables) {
    if (table.colCount === 0) continue;

    const existing = getExistingColWidths(table);
    let colWidths: number[];
    if (!existing || tableHasBadColwidth(table)) {
      colWidths = computeColWidths(table, availableWidth);
    } else if (refit) {
      // Adapt the table to the current container width, scaling columns both
      // up (full-width mode) and down (narrow mode) while keeping proportions.
      const total = existing.reduce((sum, w) => sum + w, 0);
      if (Math.abs(total - availableWidth) <= 1) continue;
      colWidths =
        total > availableWidth
          ? shrinkToFit(existing, getColFloors(table), availableWidth)
          : scaleToWidth(existing, availableWidth);
    } else {
      const total = existing.reduce((sum, w) => sum + w, 0);
      if (total <= availableWidth) continue;
      colWidths = shrinkToFit(existing, getColFloors(table), availableWidth);
    }

    for (const cell of table.cells) {
      const widthsForCell: number[] = [];
      for (let c = 0; c < cell.colspan && cell.colIndex + c < table.colCount; c++) {
        widthsForCell.push(colWidths[cell.colIndex + c]);
      }
      tr.setNodeMarkup(cell.pos, undefined, {
        ...cell.node.attrs,
        colwidth: widthsForCell
      });
      hasChanges = true;
    }
  }

  if (hasChanges) {
    tr.setMeta("addToHistory", false);
    tr.setMeta(TABLE_LAYOUT_META, true);
    editor.view.dispatch(tr);
  }
}
